/**
 * 해시태그 추출 유틸리티
 * 본문에서 #으로 시작하는 해시태그를 추출하여 Steem 호환 태그로 변환
 */

/**
 * 본문에서 Steem 호환 해시태그를 추출합니다.
 * Steem 태그 규칙: 소문자 영문으로 시작, 영문/숫자/하이픈만 허용, 1-24자
 *
 * @param content 본문 내용
 * @returns 추출된 유효한 태그 배열 (중복 제거됨)
 */
export function extractHashtags(content: string): string[] {
  // #으로 시작하고 영문으로 시작하는 태그 추출 (영문, 숫자, 하이픈 허용)
  const hashtagRegex = /#([a-zA-Z][a-zA-Z0-9-]*)/g
  const matches = content.matchAll(hashtagRegex)

  const tags: string[] = []
  for (const match of matches) {
    if (match[1]) {
      const tag = match[1].toLowerCase()
      // Steem 태그 규칙: 1-24자, 중복 제거
      if (!tags.includes(tag) && tag.length >= 1 && tag.length <= 24) {
        tags.push(tag)
      }
    }
  }

  return tags
}

/**
 * 태그 유효성 검사
 * Steem 블록체인 태그 규칙 준수 여부 확인
 *
 * @param tag 검사할 태그
 * @returns 유효한 태그면 true
 */
export function isValidSteemTag(tag: string): boolean {
  // 소문자 영문으로 시작, 영문/숫자/하이픈만 허용, 1-24자
  const tagRegex = /^[a-z][a-z0-9-]{0,23}$/
  return tagRegex.test(tag)
}

/**
 * 태그 배열을 Steem 제한에 맞게 구성합니다.
 * 기본 태그(fast-steemit)를 첫 번째로 추가하고, 추출된 태그 중 최대 4개만 추가
 *
 * @param extractedTags 추출된 해시태그 배열
 * @param defaultTag 기본 태그 (기본값: 'fast-steemit')
 * @param maxTags 최대 태그 개수 (기본값: 5)
 * @returns 구성된 태그 배열
 */
export function buildTagsArray(
  extractedTags: string[],
  defaultTag: string = 'fast-steemit',
  maxTags: number = 5
): string[] {
  const tags: string[] = [defaultTag]

  // 기본 태그를 제외한 나머지 슬롯에 추출된 태그 추가
  const remainingSlots = maxTags - 1
  const validTags = extractedTags
    .filter((tag) => tag !== defaultTag && isValidSteemTag(tag))
    .slice(0, remainingSlots)

  tags.push(...validTags)

  return tags
}
